import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Users, Clock, Briefcase, UserPlus, X, Mail, Calendar, ChevronRight, Trash2 } from 'lucide-react'
import { getTeamMembers, createTeamMember, updateTeamMember, deleteTeamMember } from '../services/team.service'
import { supabase } from '../lib/supabase'
import { useToast } from '../context/ToastContext'
import { useActivity } from '../context/ActivityContext'
import { useIsAdmin } from '../hooks/useIsAdmin'
import type { TeamMember } from '../types'

// ── Founder config ──────────────────────────────────────────
const FOUNDERS = [
  { key: 'hj',     label: 'HJ',    dept: 'Sales & Ops',  color: '#e8a000' },
  { key: 'bunny',  label: 'Bunny', dept: 'Production',   color: '#ffc600' },
  { key: 'darshu', label: 'Darshu',dept: 'Paid Ads',     color: '#ff8c00' },
] as const

// ── Add Member Modal ───────────────────────────────────────────
function AddMemberModal({ onClose, onAdd }: { onClose: () => void; onAdd: (m: Omit<TeamMember,'id'>, memberType: 'employee'|'founder', founderKey?: string, reportsTo?: string) => Promise<void> }) {
  const today = new Date().toISOString().split('T')[0]
  const [memberType, setMemberType] = useState<'employee'|'founder'>('employee')
  const [founderKey, setFounderKey] = useState<string>('')
  const [reportsTo,  setReportsTo]  = useState<string>('')
  const [form, setForm] = useState({
    name: '', role: '', email: '', skillsRaw: '',
    status: 'active' as TeamMember['status'],
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setError('') }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) { setError('Name is required.'); return }
    if (!form.role.trim()) { setError('Role / title is required.'); return }
    const emailRgx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (form.email && !emailRgx.test(form.email)) { setError('Enter a valid email.'); return }
    if (memberType === 'founder' && !founderKey) { setError('Select which founder this is.'); return }
    if (memberType === 'employee' && !reportsTo)  { setError('Select who they report to.'); return }

    setLoading(true)
    setError('')
    try {
      const skills = form.skillsRaw.split(',').map(s => s.trim()).filter(Boolean)
      await onAdd(
        { name: form.name.trim(), role: form.role.trim(), email: form.email.trim(),
          skills, utilization: 0, activeProjects: 0, hoursThisMonth: 0,
          status: form.status, joinedAt: today },
        memberType,
        memberType === 'founder' ? founderKey : undefined,
        memberType === 'employee' ? reportsTo : undefined,
      )
      onClose()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add member.')
      setLoading(false)
    }
  }

  const inputCls = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
  const inputStyle = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
  const labelStyle = { color: '#a0a0a0' }
  const focusOn  = (e: React.FocusEvent<any>) => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')
  const focusOff = (e: React.FocusEvent<any>) => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-md rounded-2xl p-6" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>Add Team Member</h2>
          <button onClick={onClose} style={{ color: '#555' }}><X size={18} /></button>
        </div>

        {error && (
          <div className="text-xs px-3 py-2 rounded-lg mb-4" style={{ background: 'rgba(204,51,0,0.12)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">

          {/* Member type toggle */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>MEMBER TYPE *</label>
            <div className="grid grid-cols-2 gap-2">
              {(['employee', 'founder'] as const).map(t => (
                <button key={t} type="button" onClick={() => { setMemberType(t); setFounderKey(''); setReportsTo('') }}
                  className="py-2 rounded-lg text-xs font-semibold capitalize transition-all"
                  style={memberType === t
                    ? { background: 'rgba(255,198,0,0.15)', color: '#ffc600', border: '1px solid rgba(255,198,0,0.4)' }
                    : { background: '#1a1a1a', color: '#555', border: '1px solid rgba(255,198,0,0.1)' }}>
                  {t === 'founder' ? '👑 Founder' : '👤 Employee'}
                </button>
              ))}
            </div>
          </div>

          {/* Founder selector OR Reports-to selector */}
          {memberType === 'founder' ? (
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>WHICH FOUNDER? *</label>
              <div className="grid grid-cols-3 gap-2">
                {FOUNDERS.map(f => (
                  <button key={f.key} type="button" onClick={() => setFounderKey(f.key)}
                    className="py-2 rounded-lg text-xs font-semibold transition-all"
                    style={founderKey === f.key
                      ? { background: `${f.color}20`, color: f.color, border: `1px solid ${f.color}60` }
                      : { background: '#1a1a1a', color: '#555', border: '1px solid rgba(255,198,0,0.1)' }}>
                    {f.label}
                    <span className="block text-[10px] mt-0.5 font-normal opacity-70">{f.dept}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>REPORTS TO *</label>
              <div className="grid grid-cols-3 gap-2">
                {FOUNDERS.map(f => (
                  <button key={f.key} type="button" onClick={() => setReportsTo(f.key)}
                    className="py-2 rounded-lg text-xs font-semibold transition-all"
                    style={reportsTo === f.key
                      ? { background: `${f.color}20`, color: f.color, border: `1px solid ${f.color}60` }
                      : { background: '#1a1a1a', color: '#555', border: '1px solid rgba(255,198,0,0.1)' }}>
                    {f.label}
                    <span className="block text-[10px] mt-0.5 font-normal opacity-70">{f.dept}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>FULL NAME *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)}
              placeholder="e.g. Ananya Sharma" className={inputCls} style={inputStyle} autoFocus
              onFocus={focusOn} onBlur={focusOff} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>TITLE / ROLE *</label>
              <input value={form.role} onChange={e => set('role', e.target.value)}
                placeholder={memberType === 'founder' ? 'e.g. Head of Paid Ads' : 'e.g. Social Media Manager'}
                className={inputCls} style={inputStyle} onFocus={focusOn} onBlur={focusOff} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>STATUS</label>
              <select value={form.status} onChange={e => set('status', e.target.value)} className={inputCls} style={inputStyle}>
                <option value="active">Active</option>
                <option value="on-leave">On Leave</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>EMAIL</label>
            <input value={form.email} onChange={e => set('email', e.target.value)}
              placeholder="ananya@clickbait.in" type="email" className={inputCls} style={inputStyle}
              onFocus={focusOn} onBlur={focusOff} />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={labelStyle}>SKILLS (comma-separated)</label>
            <input value={form.skillsRaw} onChange={e => set('skillsRaw', e.target.value)}
              placeholder="Meta Ads, Copywriting, Analytics" className={inputCls} style={inputStyle}
              onFocus={focusOn} onBlur={focusOff} />
          </div>

          {form.email && (
            <div className="text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(255,198,0,0.06)', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
              📧 Invite link will be emailed — they'll set their own password and log in directly.
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} disabled={loading}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
              style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)', opacity: loading ? 0.5 : 1 }}>
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-2"
              style={{ background: loading ? 'rgba(255,198,0,0.6)' : '#ffc600', color: '#000', cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? (
                <>
                  <span className="inline-block w-3.5 h-3.5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  {form.email ? 'Sending invite…' : 'Adding…'}
                </>
              ) : 'Add Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Member Detail Modal ────────────────────────────────────────
function MemberDetailModal({ member, onClose, onUpdate, onDelete, isAdmin }: {
  member: TeamMember; onClose: () => void
  onUpdate: (id: string, changes: Partial<TeamMember>) => void
  onDelete: (id: string) => void
  isAdmin: boolean
}) {
  const uColor = member.utilization > 90 ? '#cc3300' : member.utilization >= 75 ? '#ff8c00' : '#e8a000'
  const isActive = member.status === 'active'
  const init = member.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  const joined = new Date(member.joinedAt).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [form, setForm] = useState({ name: member.name, role: member.role, email: member.email ?? '', status: member.status, skillsRaw: member.skills.join(', ') })
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))
  const handleSave = () => {
    onUpdate(member.id, { name: form.name.trim() || member.name, role: form.role.trim() || member.role, email: form.email.trim() || undefined, status: form.status as TeamMember['status'], skills: form.skillsRaw.split(',').map(s => s.trim()).filter(Boolean) })
    setEditing(false)
  }

  const inputCls = "w-full rounded-lg px-3 py-2 text-sm text-white outline-none"
  const inputStyle = { background: '#1a1a1a', border: '1px solid rgba(255,198,0,0.2)' }
  const labelStyle = { color: '#a0a0a0', fontSize: '11px', fontWeight: 600 }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-sm rounded-2xl overflow-hidden" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.35)' }}>
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(255,198,0,0.1)' }}>
          <h2 className="text-sm font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
            {editing ? 'Edit Member' : 'Member Profile'}
          </h2>
          <div className="flex items-center gap-2">
            {!editing && isAdmin && (
              <button onClick={() => setEditing(true)}
                className="text-xs px-2.5 py-1 rounded-lg font-medium"
                style={{ background: 'rgba(255,198,0,0.1)', color: '#ffc600', border: '1px solid rgba(255,198,0,0.2)' }}>
                Edit
              </button>
            )}
            <button onClick={onClose} style={{ color: '#555' }}><X size={16} /></button>
          </div>
        </div>

        <div className="px-5 py-5">
          {editing ? (
            /* ── Edit form ── */
            <div className="space-y-3">
              <div>
                <label className="block mb-1.5" style={labelStyle}>NAME</label>
                <input value={form.name} onChange={e => set('name', e.target.value)} className={inputCls} style={inputStyle}
                  onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')} onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
              </div>
              <div>
                <label className="block mb-1.5" style={labelStyle}>ROLE</label>
                <input value={form.role} onChange={e => set('role', e.target.value)} className={inputCls} style={inputStyle}
                  onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')} onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
              </div>
              <div>
                <label className="block mb-1.5" style={labelStyle}>EMAIL</label>
                <input value={form.email} onChange={e => set('email', e.target.value)} className={inputCls} style={inputStyle}
                  onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')} onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
              </div>
              <div>
                <label className="block mb-1.5" style={labelStyle}>STATUS</label>
                <div className="flex gap-2">
                  {(['active', 'on-leave'] as const).map(s => (
                    <button key={s} onClick={() => set('status', s)}
                      className="flex-1 py-2 rounded-lg text-xs font-semibold capitalize"
                      style={form.status === s
                        ? { background: s === 'active' ? 'rgba(232,160,0,0.2)' : 'rgba(85,85,85,0.3)', color: s === 'active' ? '#e8a000' : '#a0a0a0', border: `1px solid ${s === 'active' ? 'rgba(232,160,0,0.4)' : 'rgba(85,85,85,0.4)'}` }
                        : { background: '#1a1a1a', color: '#555', border: '1px solid rgba(255,198,0,0.1)' }}>
                      {s === 'active' ? '● Active' : '● On Leave'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block mb-1.5" style={labelStyle}>SKILLS (comma separated)</label>
                <input value={form.skillsRaw} onChange={e => set('skillsRaw', e.target.value)} className={inputCls} style={inputStyle}
                  placeholder="e.g. Google Ads, SEO, Analytics"
                  onFocus={e => (e.target.style.borderColor = 'rgba(255,198,0,0.5)')} onBlur={e => (e.target.style.borderColor = 'rgba(255,198,0,0.2)')} />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => setEditing(false)}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                  Discard
                </button>
                <button onClick={handleSave}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background: '#ffc600', color: '#000' }}>
                  Save Changes
                </button>
              </div>
            </div>
          ) : (
          /* ── View mode ── */
          <div>
          {/* Avatar + name */}
          <div className="flex items-center gap-4 mb-5">
            <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0"
              style={{ background: '#ffc600', color: '#000' }}>
              {init}
            </div>
            <div>
              <p className="text-base font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{member.name}</p>
              <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>{member.role}</p>
              <span className="inline-block text-xs mt-1 px-2 py-0.5 rounded"
                style={isActive
                  ? { background: 'rgba(232,160,0,0.15)', color: '#e8a000' }
                  : { background: 'rgba(85,85,85,0.2)', color: '#555' }}>
                {isActive ? 'Active' : 'On Leave'}
              </span>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-2 mb-5">
            {[
              { label: 'Utilization', value: `${member.utilization}%`, color: uColor },
              { label: 'Projects', value: `${member.activeProjects}`, color: '#ffc600' },
              { label: 'Hours/Mo', value: `${member.hoursThisMonth}`, color: '#a0a0a0' },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded-lg px-3 py-2.5 text-center" style={{ background: '#1a1a1a' }}>
                <p className="text-base font-bold font-syne" style={{ color }}>{value}</p>
                <p className="text-xs mt-0.5" style={{ color: '#555' }}>{label}</p>
              </div>
            ))}
          </div>

          {/* Utilization bar */}
          <div className="mb-5">
            <div className="h-2 rounded-full" style={{ background: '#1a1a1a' }}>
              <div className="h-full rounded-full" style={{ width: `${member.utilization}%`, background: uColor }} />
            </div>
          </div>

          {/* Info rows */}
          <div className="space-y-2.5 mb-5">
            {member.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail size={13} style={{ color: '#555' }} />
                <span style={{ color: '#a0a0a0' }}>{member.email}</span>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <Calendar size={13} style={{ color: '#555' }} />
              <span style={{ color: '#a0a0a0' }}>Joined {joined}</span>
            </div>
          </div>

          {/* Skills */}
          <div>
            <p className="text-xs font-semibold mb-2" style={{ color: '#555' }}>SKILLS</p>
            <div className="flex flex-wrap gap-1.5">
              {member.skills.map(skill => (
                <span key={skill} className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(255,198,0,0.08)', color: '#ffc600' }}>
                  {skill}
                </span>
              ))}
            </div>
          </div>
          </div>
          )}
        </div>

        {!editing && isAdmin && (
          <div className="px-5 pb-5">
            {confirmDelete ? (
              <div className="flex gap-2">
                <button onClick={() => setConfirmDelete(false)}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                  Cancel
                </button>
                <button onClick={() => { onDelete(member.id); onClose() }}
                  className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background: 'rgba(204,51,0,0.15)', color: '#cc3300', border: '1px solid rgba(204,51,0,0.3)' }}>
                  Yes, Remove
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => setConfirmDelete(true)}
                  className="flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold flex-shrink-0"
                  style={{ background: '#1a1a1a', color: '#cc3300', border: '1px solid rgba(204,51,0,0.2)' }}>
                  <Trash2 size={14} />
                </button>
                <button onClick={onClose} className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                  style={{ background: '#1a1a1a', color: '#a0a0a0', border: '1px solid rgba(255,198,0,0.12)' }}>
                  Close
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function utilizationColor(u: number) {
  if (u > 90) return '#cc3300'
  if (u >= 75) return '#ff8c00'
  return '#e8a000'
}

function initials(name: string) {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
}

export default function Team() {
  const [members, setMembers] = useState<TeamMember[]>([])
  const [showAdd, setShowAdd]   = useState(false)
  const [selected, setSelected] = useState<TeamMember | null>(null)
  const { toast }       = useToast()
  const { addActivity } = useActivity()
  const location        = useLocation()
  const isAdmin         = useIsAdmin()

  useEffect(() => {
    getTeamMembers().then(setMembers)
    if ((location.state as any)?.openAdd) {
      setShowAdd(true)
      window.history.replaceState({}, '')
    }
  }, [])

  // Realtime: refresh members whenever team_members or projects change
  // (keeps activeProjects / utilization counts live without page reload)
  useEffect(() => {
    const refresh = () => getTeamMembers().then(setMembers).catch(console.error)

    const membersCh = supabase
      .channel('team-members-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'team_members' }, refresh)
      .subscribe()

    const projectsCh = supabase
      .channel('team-projects-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'projects' }, refresh)
      .subscribe()

    return () => {
      supabase.removeChannel(membersCh)
      supabase.removeChannel(projectsCh)
    }
  }, [])

  const avgUtil       = members.length ? Math.round(members.reduce((s, m) => s + m.utilization, 0) / members.length) : 0
  const totalProjects = members.reduce((s, m) => s + m.activeProjects, 0)
  const totalHours    = members.reduce((s, m) => s + m.hoursThisMonth, 0)

  const handleAdd = async (
    m: Omit<TeamMember, 'id'>,
    memberType: 'employee' | 'founder' = 'employee',
    founderKey?: string,
    reportsTo?: string,
  ): Promise<void> => {
    // 1. Save to team_members table
    const saved = await createTeamMember(m)
    setMembers(prev => [saved, ...prev])

    // 2. If email provided, create Supabase auth user + send invite
    if (m.email) {
      try {
        const res = await fetch('https://erp.clickbait.services/api/invite-user', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: m.email,
            name:  m.name,
            role:  memberType,
            ...(founderKey ? { founderKey } : {}),
            ...(reportsTo  ? { reportsTo  } : {}),
          }),
        })
        const json = await res.json()
        if (!res.ok) throw new Error(json.error || 'Invite failed')
        toast(`${saved.name} added — invite sent to ${m.email}`)
      } catch (inviteErr) {
        console.warn('Invite email failed:', inviteErr)
        toast(`${saved.name} added (invite email failed)`, 'info')
      }
    } else {
      toast(`${saved.name} added to the team`)
    }

    addActivity({
      type: 'new_member',
      emoji: memberType === 'founder' ? '👑' : '👤',
      description: `${saved.name} joined the team`,
      detail: `${saved.role}${founderKey ? ` · ${founderKey.toUpperCase()} (Founder)` : reportsTo ? ` · ${reportsTo.toUpperCase()}'s team` : ''}`,
    })
  }

  const handleUpdate = async (id: string, changes: Partial<TeamMember>) => {
    try {
      await updateTeamMember(id, changes)
      setMembers(prev => prev.map(m => m.id === id ? { ...m, ...changes } : m))
      setSelected(prev => prev?.id === id ? { ...prev, ...changes } as TeamMember : prev)
      const m = members.find(m => m.id === id)
      if (m) toast(`${changes.name ?? m.name} updated`)
    } catch { toast('Failed to update member', 'error') }
  }

  return (
    <div className="p-6 min-h-full" style={{ background: '#000' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-syne text-white">Team</h1>
          <p className="text-sm mt-0.5" style={{ color: '#a0a0a0' }}>{members.length} members</p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{ background: '#ffc600', color: '#000' }}>
            <UserPlus size={15} /> Add Member
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Avg Utilization', value: `${avgUtil}%`, icon: Users, color: utilizationColor(avgUtil) },
          { label: 'Active Projects', value: totalProjects.toString(), icon: Briefcase, color: '#ffc600' },
          { label: 'Hours This Month', value: totalHours.toLocaleString('en-IN'), icon: Clock, color: '#e8a000' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="rounded-xl p-4" style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}>
            <div className="flex items-center gap-2 mb-1">
              <Icon size={14} style={{ color }} />
              <p className="text-xs" style={{ color: '#a0a0a0' }}>{label}</p>
            </div>
            <p className="text-xl font-bold font-syne" style={{ color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {members.map(member => {
          const uColor   = utilizationColor(member.utilization)
          const isActive = member.status === 'active'
          return (
            <div key={member.id}
              className="rounded-xl p-5 transition-all cursor-pointer group"
              style={{ background: '#0f0f0f', border: '1px solid rgba(255,198,0,0.12)' }}
              onClick={() => setSelected(member)}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.3)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,198,0,0.12)')}>

              {/* Avatar + info */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                  style={{ background: '#ffc600', color: '#000' }}>
                  {initials(member.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold font-syne text-white truncate">{member.name}</p>
                    <span className="text-xs px-1.5 py-0.5 rounded flex-shrink-0"
                      style={isActive
                        ? { background: 'rgba(232,160,0,0.15)', color: '#e8a000' }
                        : { background: 'rgba(85,85,85,0.2)', color: '#555' }}>
                      {isActive ? 'Active' : 'On Leave'}
                    </span>
                  </div>
                  <p className="text-xs mt-0.5" style={{ color: '#a0a0a0' }}>{member.role}</p>
                </div>
                <ChevronRight size={14} className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#555' }} />
              </div>

              {/* Utilization bar */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs" style={{ color: '#a0a0a0' }}>Utilization</span>
                  <span className="text-xs font-semibold" style={{ color: uColor }}>{member.utilization}%</span>
                </div>
                <div className="h-1.5 rounded-full" style={{ background: '#1a1a1a' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${member.utilization}%`, background: uColor }} />
                </div>
              </div>

              {/* Stats row */}
              <div className="flex gap-3 mb-3 text-xs" style={{ color: '#a0a0a0' }}>
                <span className="flex items-center gap-1"><Briefcase size={11} />{member.activeProjects} projects</span>
                <span className="flex items-center gap-1"><Clock size={11} />{member.hoursThisMonth} hrs</span>
              </div>

              {/* Skills */}
              <div className="flex flex-wrap gap-1.5">
                {member.skills.slice(0, 4).map(skill => (
                  <span key={skill} className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(255,198,0,0.08)', color: '#ffc600' }}>
                    {skill}
                  </span>
                ))}
                {member.skills.length > 4 && (
                  <span className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(85,85,85,0.2)', color: '#555' }}>
                    +{member.skills.length - 4}
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {showAdd && <AddMemberModal onClose={() => setShowAdd(false)} onAdd={(m, mt, fk, rt) => handleAdd(m, mt, fk, rt)} />}
      {selected && (
        <MemberDetailModal
          member={selected}
          onClose={() => setSelected(null)}
          onUpdate={handleUpdate}
          isAdmin={isAdmin}
          onDelete={async id => {
            try {
              await deleteTeamMember(id)
              const m = members.find(x => x.id === id)
              setMembers(prev => prev.filter(x => x.id !== id))
              setSelected(null)
              if (m) {
                toast(`${m.name} removed from team`, 'info')
                addActivity({ type: 'member_removed', emoji: '👋', description: `${m.name} removed from team`, detail: m.role })
              }
            } catch { toast('Failed to remove member', 'error') }
          }}
        />
      )}
    </div>
  )
}
